import type { FC, ReactNode } from 'react';
import { Flex, Tag, TagCloseButton, TagLabel } from '@chakra-ui/react';
import { defineMessage, useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { ClientOnly } from '../../../../components/client-only/ClientOnly';
import { CloseIcon } from '../../../../components/icons';
import { DEFAULT_CURRENCY } from '../../../../constants';
import { AdaptiveRevealList } from './AdaptiveRevealList';
import type { CategoryNode, UseSearchResult } from '../../hooks/use-search';
import type { RevealMode } from './AdaptiveRevealList';

type FilterTagsProps = {
    search: UseSearchResult;
    extraItem?: ReactNode;
    className?: string;
} & RevealMode;

const allCategory = defineMessage({
    id: 'search.filters.category.all-category',
    defaultMessage: 'All {categoryName}',
});

const formatLabel = (
    type: string,
    value: string,
    label: string,
    intl: IntlShape,
    categoriesMap: Record<string, CategoryNode>,
): string => {
    if (type === 'price') {
        const [minPrice, maxPrice] = value.split('-');

        const formattedMin =
            minPrice === '*'
                ? ''
                : intl.formatNumber(Number(minPrice), {
                      style: 'currency',
                      currency: DEFAULT_CURRENCY,
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                  });

        const formattedMax =
            maxPrice === '*'
                ? ''
                : intl.formatNumber(Number(maxPrice), {
                      style: 'currency',
                      currency: DEFAULT_CURRENCY,
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                  });

        if (!formattedMin) {
            return intl.formatMessage(
                { id: 'search.filters.under-max', defaultMessage: 'Under {price}' },
                { price: formattedMax },
            );
        }

        if (!formattedMax) {
            return intl.formatMessage(
                { id: 'search.filters.over-max', defaultMessage: 'Over {price}' },
                { price: formattedMin },
            );
        }

        return `${formattedMin} - ${formattedMax}`;
    }

    if (type === 'rating') {
        const [start] = value.split('-');
        if (start === '5') {
            return intl.formatMessage(
                { id: 'search.filters.rating.5', defaultMessage: '{stars} Stars' },
                { stars: start },
            );
        }
        return intl.formatMessage(
            { id: 'search.filters.rating.up', defaultMessage: '{stars} & Up Stars' },
            { stars: start },
        );
    }

    if (type === 'category') {
        if (categoriesMap[value].type === 'T2') {
            return intl.formatMessage(allCategory, { categoryName: label });
        }
        return label;
    }

    return label;
};

export const FilterTags: FC<FilterTagsProps> = ({
    search: { activeFilters, onRemoveFilter, categoriesMap },
    className,
    ...props
}) => {
    const intl = useIntl();

    if (activeFilters.length === 0) return null;

    return (
        <ClientOnly>
            <Flex data-testid="filter-tags" maxWidth="100%" className={className}>
                <AdaptiveRevealList
                    {...props}
                    data={activeFilters}
                    keyExtractor={(filter) => `${filter.type}-${filter.value}`}
                    renderItem={({ item: filter }) => {
                        const typeAndValue = `${filter.type}-${filter.value
                            .toLowerCase()
                            .replace(/ /g, '')}`;

                        return (
                            <Tag
                                data-testid={`filter-tags-tag-${typeAndValue}`}
                                key={`${filter.type}-${filter.value}`}
                            >
                                <TagLabel>
                                    {formatLabel(
                                        filter.type,
                                        filter.value,
                                        filter.label,
                                        intl,
                                        categoriesMap,
                                    )}
                                </TagLabel>
                                <TagCloseButton
                                    data-testid={`filter-tags-tag-remove-button-${typeAndValue}`}
                                    onClick={() => onRemoveFilter(filter.type, filter.value)}
                                >
                                    <CloseIcon width="8px" height="8px" />
                                </TagCloseButton>
                            </Tag>
                        );
                    }}
                />
            </Flex>
        </ClientOnly>
    );
};

FilterTags.defaultProps = {
    extraItem: null,
    className: undefined,
};
